import React from 'react'
import { useWebSocketConnection } from '@/hooks/useWebSocket'
import { Button } from '@/components/ui/button'
import { 
  Wifi, 
  WifiOff, 
  AlertTriangle, 
  RefreshCw, 
  CheckCircle2,
  XCircle 
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ConnectionStatusProps {
  className?: string
  showReconnectButton?: boolean
  onReconnect?: () => void
}

export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  className,
  showReconnectButton = true,
  onReconnect
}) => {
  const connection = useWebSocketConnection()

  const getStatusInfo = () => {
    if (connection.isOnline) {
      return {
        icon: <CheckCircle2 className="w-4 h-4" />,
        text: 'Connected',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      }
    }

    if (connection.isReconnecting) {
      return {
        icon: <RefreshCw className="w-4 h-4 animate-spin" />,
        text: `Reconnecting... (${connection.reconnectAttempts}/${connection.maxReconnectAttempts})`,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      }
    }

    if (connection.connecting) {
      return {
        icon: <RefreshCw className="w-4 h-4 animate-spin" />,
        text: 'Connecting...',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      }
    }

    if (connection.hasError) {
      return {
        icon: <XCircle className="w-4 h-4" />,
        text: connection.error || 'Connection failed',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      }
    }

    return {
      icon: <WifiOff className="w-4 h-4" />,
      text: 'Disconnected',
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      borderColor: 'border-border'
    }
  }

  const statusInfo = getStatusInfo()

  // Don't show anything if connected and no error
  if (connection.isOnline && !connection.hasError) {
    return null
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 border rounded-lg',
        statusInfo.bgColor,
        statusInfo.borderColor,
        className
      )}
    >
      <div className={cn('flex items-center gap-2', statusInfo.color)}>
        {statusInfo.icon}
        <span className="text-sm font-medium">{statusInfo.text}</span>
      </div>

      {showReconnectButton && !connection.isOnline && !connection.connecting && !connection.isReconnecting && onReconnect && (
        <Button
          variant="outline"
          size="sm"
          onClick={onReconnect}
          className="ml-3"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Retry
        </Button>
      )}
    </div>
  )
}

// Compact version for header/status bar
export const CompactConnectionStatus: React.FC<{ className?: string }> = ({ className }) => {
  const connection = useWebSocketConnection()

  if (connection.isOnline) {
    return (
      <div className={cn('flex items-center gap-1 text-green-600', className)}>
        <Wifi className="w-3 h-3" />
        <span className="text-xs">Live</span>
      </div>
    )
  }

  if (connection.isReconnecting || connection.connecting) {
    return (
      <div className={cn('flex items-center gap-1 text-yellow-600', className)}>
        <RefreshCw className="w-3 h-3 animate-spin" />
        <span className="text-xs">Connecting</span>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-1 text-red-600', className)}>
      <WifiOff className="w-3 h-3" />
      <span className="text-xs">Offline</span>
    </div>
  )
}

// Typing indicator component
interface TypingIndicatorProps {
  typingUsers: string[]
  className?: string
}

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({ 
  typingUsers, 
  className 
}) => {
  if (typingUsers.length === 0) {
    return null
  }

  const getUsersText = () => {
    if (typingUsers.length === 1) {
      return 'Someone is typing...'
    }
    return `${typingUsers.length} people are typing...`
  }

  return (
    <div className={cn('flex items-center gap-2 text-muted-foreground text-sm italic', className)}>
      <div className="flex gap-1">
        <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" />
        <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
        <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
      </div>
      <span>{getUsersText()}</span>
    </div>
  )
}

export default ConnectionStatus